import React, { useState } from 'react';
import { useBodyScrollLock } from '../../lib/useBodyScrollLock';
import Modal from '../shared/Modal';
import { InvoiceRecord, WorkspaceBranding } from '../../types';
import { auth } from '../../lib/firebase';
import {
  X, Printer, Mail, DollarSign, Download, FileText, CheckCircle2, Clock, AlertTriangle, XCircle, CreditCard, Loader, Sparkles, Check, MoreVertical
} from 'lucide-react';
import { workspaceHeader } from '../../lib/workspaces';
import { sanitizeEmailHtml } from './email/emailSanitize';
import { downloadPdf, triggerBlobDownload } from '../../lib/api';
import { buildApplicantPdfFileName } from '../../lib/pdfFileName';
import { confirmDialog } from '../../lib/confirmStore';
import { notifyToast } from '../../lib/toastStore';

const wsFetch = (url: string, options?: RequestInit): Promise<Response> => {
  return fetch(url, {
    ...options,
    headers: { ...workspaceHeader(), ...(options?.headers as Record<string, string>) },
  });
};


interface InvoicePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  invoice: InvoiceRecord | null;
  branding?: WorkspaceBranding;
  financeSettings?: { billingEmail?: string; invoiceFooter?: string };
  onSendEmail?: (invoiceId: string) => Promise<void>;
  onRecordPayment?: (invoice: InvoiceRecord) => void;
  onDisassociatePayment?: (allocationId: string, amount: number) => Promise<void>;
  onOpenPaymentDetail?: (paymentId: string) => void;
  isAdminView?: boolean;
}

export default function InvoicePreviewModal({
  isOpen,
  onClose,
  invoice,
  branding,
  financeSettings,
  onSendEmail,
  onRecordPayment,
  onDisassociatePayment,
  onOpenPaymentDetail,
  isAdminView = true,
}: InvoicePreviewModalProps) {
  useBodyScrollLock(isOpen);

  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [isGeneratingLink, setIsGeneratingLink] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [confirmSendEmailOpen, setConfirmSendEmailOpen] = useState(false);
  const [showMoreActions, setShowMoreActions] = useState(false);

  if (!isOpen || !invoice) return null;

  const handleGeneratePaymentLink = async () => {
    setIsGeneratingLink(true);
    setCopiedLink(false);
    try {
      const invId = invoice.id || invoice.invoiceId;
      const token = await auth.currentUser?.getIdToken();
      const res = await wsFetch(`/api/admin/finance/invoices/${invId}/payment-link`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to generate payment link');

      if (data.paymentUrl) {
        await navigator.clipboard.writeText(data.paymentUrl);
        setCopiedLink(true);
        setTimeout(() => setCopiedLink(false), 3000);
      }
    } catch (err: any) {
      notifyToast('error', err.message || 'Error generating payment link');
    } finally {
      setIsGeneratingLink(false);
    }
  };

  const handleDownloadPdf = async () => {
    setIsGeneratingPdf(true);
    try {
      const invId = invoice.id || invoice.invoiceId;
      const blob = await downloadPdf(`/api/finance/invoices/${invId}/pdf`);
      triggerBlobDownload(blob, buildApplicantPdfFileName(`Invoice-${invoice.invoiceNumber || invId}`, invoice.applicantId || (invoice as any).applicationId, invoice.applicantName));
    } catch (e: any) {
      notifyToast('error', e?.message || 'Failed to generate PDF. Please try again or use Print instead.');
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleSendEmailPrompt = () => {
    setConfirmSendEmailOpen(true);
  };

  const executeSendEmail = async () => {
    const invId = invoice.id || invoice.invoiceId;
    if (!invId) return;
    setIsSendingEmail(true);
    setEmailSuccess(false);
    try {
      if (onSendEmail) {
        await onSendEmail(invId);
      } else {
        const token = await auth.currentUser?.getIdToken();
        const res = await wsFetch(`/api/admin/finance/invoices/${invId}/email`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'Failed to dispatch invoice email');
      }
      setEmailSuccess(true);
      setTimeout(() => setEmailSuccess(false), 4000);
    } catch (err: any) {
      notifyToast('error', err.message || 'Error sending invoice email');
    } finally {
      setIsSendingEmail(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'Paid':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-md text-[10px] font-extrabold uppercase tracking-wider bg-emerald-50 text-emerald-800 border border-emerald-200">
            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> Paid
          </span>
        );
      case 'Partially Paid':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-md text-[10px] font-extrabold uppercase tracking-wider bg-amber-50 text-amber-800 border border-amber-200">
            <Clock className="h-3.5 w-3.5 text-amber-600" /> Partially Paid
          </span>
        );
      case 'Overdue':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-md text-[10px] font-extrabold uppercase tracking-wider bg-rose-50 text-rose-800 border border-rose-200">
            <AlertTriangle className="h-3.5 w-3.5 text-rose-600" /> Overdue
          </span>
        );
      case 'Sent':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-md text-[10px] font-extrabold uppercase tracking-wider bg-blue-50 text-blue-800 border border-blue-200">
            <Mail className="h-3.5 w-3.5 text-blue-600" /> Issued / Sent
          </span>
        );
      case 'Cancelled':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-md text-[10px] font-extrabold uppercase tracking-wider bg-slate-100 text-slate-300 border border-slate-200">
            <XCircle className="h-3.5 w-3.5 text-slate-300" /> Cancelled
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-md text-[10px] font-extrabold uppercase tracking-wider bg-slate-50 text-slate-700 border border-slate-200">
            Draft
          </span>
        );
    }
  };

  return (
    <Modal onClose={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden text-slate-800 animate-fade-in printable-area">
        {/* Top Control Bar (Non-Printable) */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 px-6 py-4 border-b border-slate-200 bg-white text-slate-900 print:hidden">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center font-bold shrink-0">
              <FileText className="h-5 w-5" />
            </div>
            <div>
              <h3 className="text-base font-extrabold text-slate-900 font-display tracking-tight">
                Invoice #{invoice.invoiceNumber || invoice.id.slice(0, 8)}
              </h3>
              <p className="text-[11px] text-slate-500">Manage, export, and record transactions for this invoice</p>
            </div>
            <div className="ml-1">
              {getStatusBadge(invoice.status)}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {isAdminView && onRecordPayment && invoice.amountDue > 0 && invoice.status !== 'Cancelled' ? (
              <button
                onClick={() => onRecordPayment(invoice)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-success hover:bg-[color-mix(in_srgb,var(--color-success)_90%,#000)] text-white font-bold rounded-xl text-xs transition-all cursor-pointer shadow-sm"
              >
                <CreditCard className="h-3.5 w-3.5" />
                <span>Record Payment</span>
              </button>
            ) : isAdminView ? (
              <button
                onClick={handleSendEmailPrompt}
                disabled={isSendingEmail}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-brand-primary hover:bg-brand-primary-dark text-white font-bold rounded-xl text-xs transition-all cursor-pointer disabled:opacity-50 shadow-sm"
              >
                <Mail className="h-3.5 w-3.5" />
                <span>{isSendingEmail ? 'Sending...' : 'Email Invoice'}</span>
              </button>
            ) : null}

            {/* Overflow "More" menu */}
            <div className="relative">
              <button
                onClick={() => setShowMoreActions(!showMoreActions)}
                className="p-1.5 bg-white hover:bg-slate-50 text-slate-600 border border-slate-200 rounded-xl transition-all cursor-pointer flex items-center gap-1"
                title="More Actions"
              >
                <MoreVertical className="h-4 w-4" />
              </button>

              {showMoreActions && (
                <div className="absolute right-0 top-full mt-1.5 w-48 bg-white border border-slate-200 rounded-xl shadow-xl p-1.5 z-50 text-xs font-bold space-y-1">
                  {isAdminView && (
                    <button
                      onClick={() => { setShowMoreActions(false); handleSendEmailPrompt(); }}
                      disabled={isSendingEmail}
                      className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 rounded-lg transition-colors flex items-center gap-2 cursor-pointer"
                    >
                      <Mail className="h-3.5 w-3.5 text-blue-600" />
                      <span>{isSendingEmail ? 'Sending...' : 'Email Invoice'}</span>
                    </button>
                  )}
                  {isAdminView && (
                    <button
                      onClick={() => { setShowMoreActions(false); handleGeneratePaymentLink(); }}
                      disabled={isGeneratingLink}
                      className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 rounded-lg transition-colors flex items-center gap-2 cursor-pointer"
                    >
                      <Sparkles className="h-3.5 w-3.5 text-purple-600" />
                      <span>{copiedLink ? 'Link Copied!' : 'Payment Link'}</span>
                    </button>
                  )}
                  <button
                    onClick={() => { setShowMoreActions(false); handleDownloadPdf(); }}
                    disabled={isGeneratingPdf}
                    className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 rounded-lg transition-colors flex items-center gap-2 cursor-pointer"
                  >
                    <Download className="h-3.5 w-3.5 text-emerald-600" />
                    <span>Download PDF</span>
                  </button>
                  <button
                    onClick={() => { setShowMoreActions(false); handlePrint(); }}
                    className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 rounded-lg transition-colors flex items-center gap-2 cursor-pointer"
                  >
                    <Printer className="h-3.5 w-3.5 text-slate-500" />
                    <span>Print</span>
                  </button>
                </div>
              )}
            </div>

            <button aria-label="Close" title="Close" onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-xl transition-all cursor-pointer ml-1">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {emailSuccess && (
          <div className="px-6 py-2 bg-emerald-50 text-emerald-800 text-xs font-bold border-b border-emerald-200 flex items-center gap-2 print:hidden">
            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
            Invoice dispatched successfully to {invoice.applicantEmail}.
          </div>
        )}

        {/* Invoice Printable View Content */}
        <div className="p-8 space-y-8 overflow-y-auto flex-1 text-slate-800 font-sans print:p-0">
          {/* Header Branding & Invoice Meta */}
          <div className="flex justify-between items-start pb-6 border-b border-slate-200">
            <div className="space-y-2">
              {branding?.invoiceLogoUrl || branding?.logoUrl || branding?.loginLogoUrl || branding?.emailLogoUrl ? (
                <img src={branding.invoiceLogoUrl || branding.logoUrl || branding.loginLogoUrl || branding.emailLogoUrl} alt="Logo" className="h-10 object-contain" />
              ) : (
                <div className="text-xl font-black text-slate-900 tracking-tight font-display">
                  {branding?.businessName || 'ISATS Portal'}
                </div>
              )}
              <div className="text-xs text-slate-300 leading-relaxed max-w-xs">
                {branding?.businessName || 'International Exchange Services'}<br />
                Billing Contact: {financeSettings?.billingEmail || branding?.supportEmail || 'billing@isats.org'}
              </div>
            </div>

            <div className="text-right space-y-1">
              <h1 className="text-2xl font-black text-white font-display font-black uppercase tracking-wider">INVOICE</h1>
              <p className="text-xs font-mono font-bold text-slate-600">#{invoice.invoiceNumber || invoice.id}</p>
              <div className="text-xs text-slate-500 pt-1">
                <div><span className="font-semibold">Invoice Date:</span> {invoice.invoiceDate ? new Date(invoice.invoiceDate).toLocaleDateString() : '-'}</div>
                <div><span className="font-semibold">Due Date:</span> {invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString() : '-'}</div>
              </div>
            </div>
          </div>

          {/* Bill To & Details */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 bg-slate-50 p-5 rounded-2xl border border-slate-100 print:bg-transparent print:p-0 text-xs">
            <div>
              <span className="text-[10px] font-black uppercase text-slate-400 tracking-wider block mb-1">Billed To</span>
              <h4 className="font-extrabold text-slate-900">{invoice.applicantName}</h4>
              <p className="text-slate-600 truncate">{invoice.applicantEmail}</p>
              {invoice.quoteNumber && (
                <p className="text-[10px] text-purple-700 font-bold mt-1">Originated from Quote #{invoice.quoteNumber}</p>
              )}
            </div>

            <div>
              <span className="text-[10px] font-black uppercase text-slate-400 tracking-wider block mb-1">Program & Term</span>
              <p className="font-bold text-slate-800">{invoice.programName || 'General Program'}</p>
              <p className="text-slate-500 font-mono text-[11px]">{invoice.term || 'N/A'}</p>
            </div>

            <div>
              <span className="text-[10px] font-black uppercase text-slate-400 tracking-wider block mb-1">Referrer</span>
              <p className="font-bold text-slate-800">{invoice.referrerName || 'Direct Applicant'}</p>
            </div>

            <div className="text-right">
              <span className="text-[10px] font-black uppercase text-slate-400 tracking-wider block mb-1">Sponsor</span>
              <p className="font-bold text-slate-800">{invoice.sponsorName || 'Unassigned'}</p>
              <div className="mt-1">
                <span className="text-[10px] font-bold uppercase text-slate-400 block">Payment Terms</span>
                <span className="font-semibold text-slate-700">Net 14 Days</span>
              </div>
            </div>
          </div>

          {/* Line Items Table */}
          <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-[10px] font-black uppercase text-slate-400 tracking-wider">
                  <th className="p-3 w-1/2">Item Description</th>
                  <th className="p-3 text-center">Qty</th>
                  <th className="p-3 text-right">Unit Rate</th>
                  <th className="p-3 text-right">Discount</th>
                  <th className="p-3 text-right">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-xs font-medium">
                {(invoice.items || []).map((item, idx) => (
                  <tr key={item.id || idx}>
                    <td className="p-3">
                      <div className="font-bold text-slate-900 break-words">{item.name}</div>
                      {item.description && (
                        <div className="text-[11px] text-slate-500 mt-0.5 whitespace-pre-line break-words">{item.description}</div>
                      )}
                    </td>
                    <td className="p-3 text-center font-bold text-slate-700">{item.quantity || 1}</td>
                    <td className="p-3 text-right font-mono text-slate-700">${(item.rate || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td className="p-3 text-right font-mono text-emerald-600">
                      {item.discountAmount > 0 ? `-$${item.discountAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                    </td>
                    <td className="p-3 text-right font-mono font-bold text-slate-900">${(item.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  </tr>
                ))}
                {(!invoice.items || invoice.items.length === 0) && (
                  <tr>
                    <td colSpan={5} className="p-4 text-center text-slate-400">
                      Invoice for {invoice.applicantName || 'Applicant'} - Amount: ${(invoice.total || invoice.amount || 0).toFixed(2)}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Financial Totals Block */}
          <div className="flex justify-end pt-2">
            <div className="w-full max-w-xs space-y-2 text-xs">
              <div className="flex justify-between text-slate-600">
                <span>Subtotal:</span>
                <span className="font-mono font-bold">${(invoice.subtotal || invoice.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
              {invoice.totalDiscount > 0 && (
                <div className="flex justify-between text-emerald-600">
                  <span>Line Item Discounts:</span>
                  <span className="font-mono font-bold">-${(invoice.totalDiscount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
              )}
              {invoice.adjustment !== undefined && invoice.adjustment !== 0 && (
                <div className="flex justify-between text-indigo-600">
                  <span>Adjustment ({invoice.adjustmentReason || 'Manual'}):</span>
                  <span className="font-mono font-bold">{invoice.adjustment > 0 ? '+' : ''}${(invoice.adjustment || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
              )}
              <hr className="border-slate-200 my-1" />
              <div className="flex justify-between font-extrabold text-sm text-slate-900">
                <span>Total Amount:</span>
                <span className="font-mono">${(invoice.total || invoice.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {invoice.currency || 'USD'}</span>
              </div>

              <div className="flex justify-between text-emerald-700 font-bold bg-emerald-50/80 p-2 rounded-lg my-1">
                <span>Amount Paid:</span>
                <span className="font-mono">-${(invoice.amountPaid || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>

              <div className="flex justify-between font-black text-base text-blue-600 border-t-2 border-slate-900 pt-2">
                <span>Balance Due:</span>
                <span className="font-mono">${(invoice.amountDue ?? (invoice.total || invoice.amount || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {invoice.currency || 'USD'}</span>
              </div>
            </div>
          </div>

          {/* Payment History Block */}
          {Array.isArray(invoice.payments) && invoice.payments.length > 0 && (
            <div className="pt-4 border-t border-slate-200">
              <h5 className="text-[10px] font-black uppercase text-slate-400 tracking-wider mb-2">Payment Allocations & History</h5>
              <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
                <table className="w-full text-left border-collapse text-xs">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200 text-[10px] font-black uppercase text-slate-400 tracking-wider">
                      <th className="p-2.5">Payment ID</th>
                      <th className="p-2.5">Date</th>
                      <th className="p-2.5">Method</th>
                      <th className="p-2.5">Reference / Txn #</th>
                      <th className="p-2.5 text-right">Amount Applied</th>
                      {isAdminView && <th className="p-2.5 text-right print:hidden">Action</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 font-medium">
                    {invoice.payments.map((p: any, idx: number) => (
                      <tr key={p.allocationId || p.paymentId || idx}>
                        <td className="p-2.5 font-mono font-bold text-slate-900">
                          {onOpenPaymentDetail && p.paymentId ? (
                            <button
                              type="button"
                              onClick={() => onOpenPaymentDetail(p.paymentId)}
                              className="text-blue-600 hover:text-blue-800 hover:underline font-bold text-xs cursor-pointer inline-flex items-center gap-1"
                            >
                              {p.paymentId}
                            </button>
                          ) : (
                            <span>{p.paymentId || '-'}</span>
                          )}
                        </td>
                        <td className="p-2.5 text-slate-600">{p.recordedAt ? new Date(p.recordedAt).toLocaleDateString() : '-'}</td>
                        <td className="p-2.5 font-bold text-slate-800">{p.paymentMethod || 'Payment'}</td>
                        <td className="p-2.5 font-mono text-slate-500">{p.paymentReference || '-'}</td>
                        <td className="p-2.5 text-right font-mono font-bold text-emerald-600">-${(Number(p.amount) || 0).toFixed(2)}</td>
                        {isAdminView && (
                          <td className="p-2.5 text-right print:hidden">
                            {onDisassociatePayment && (p.allocationId || p.paymentId) ? (
                              <button
                                type="button"
                                onClick={async () => {
                                  const allocId = p.allocationId || p.paymentId;
                                  const amtStr = (Number(p.amount) || 0).toFixed(2);
                                  const confirmMsg = `Remove from Invoice?\n\nThis will remove the $${amtStr} payment from Invoice #${invoice.invoiceNumber}. The payment record will be KEPT and the funds will become unapplied credit on the applicant's account.\n\nDo you want to proceed?`;
                                  if (await confirmDialog(confirmMsg)) {
                                    try {
                                      await onDisassociatePayment(allocId, Number(p.amount) || 0);
                                    } catch (err: any) {
                                      notifyToast('error', err.message || 'Failed to remove payment from invoice');
                                    }
                                  }
                                }}
                                className="px-2 py-1 bg-amber-50 hover:bg-amber-100 text-amber-800 rounded-lg text-[10px] font-bold transition-all cursor-pointer border border-amber-200"
                              >
                                Remove from Invoice
                              </button>
                            ) : (
                              <span className="text-slate-300">-</span>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Notes & Terms */}
          {invoice.notes && (
            <div className="pt-4 border-t border-slate-200">
              <h5 className="text-[10px] font-black uppercase text-slate-400 tracking-wider mb-1">Notes & Instructions</h5>
              <p className="text-xs text-slate-600 leading-relaxed bg-slate-50 p-3 rounded-xl">{invoice.notes}</p>
            </div>
          )}

          {/* Internal Confidential Notes (Admins Only) */}
          {isAdminView && invoice.internalNotes && (
            <div className="pt-4 border-t border-slate-200 print:hidden">
              <h5 className="text-[10px] font-black uppercase text-rose-500 tracking-wider mb-1">Internal Admin Note (Hidden from Customer)</h5>
              <p className="text-xs text-slate-700 bg-rose-50/60 border border-rose-100 p-3 rounded-xl italic">{invoice.internalNotes}</p>
            </div>
          )}

          {/* Invoice Footer */}
          {financeSettings?.invoiceFooter && (
            <div
              className="pt-4 border-t border-slate-200 text-center text-[11px] text-slate-500 leading-relaxed prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: sanitizeEmailHtml(financeSettings.invoiceFooter) }}
            />
          )}
        </div>
      </div>

      {confirmSendEmailOpen && (
        <Modal onClose={() => setConfirmSendEmailOpen(false)} ariaLabel="Confirm Email Invoice">
          <div className="bg-white rounded-2xl p-6 shadow-2xl max-w-md w-full space-y-4">
            <div className="flex items-center space-x-3">
              <div className="p-3 rounded-2xl bg-blue-50 text-blue-600">
                <Mail className="h-6 w-6" />
              </div>
              <div>
                <h3 className="text-base font-extrabold text-slate-900">Send Invoice Email Now?</h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Send Invoice #{invoice.invoiceNumber || invoice.id} to <strong>{invoice.applicantEmail}</strong>?
                </p>
              </div>
            </div>
            <div className="bg-slate-50 rounded-xl p-3 border border-slate-200 text-xs text-slate-600 space-y-1">
              <div>• Attachments: <strong>Invoice_{invoice.invoiceNumber || invoice.id}.pdf</strong></div>
              <div>• Payment Link: <strong>Direct Online Payment Link</strong></div>
            </div>
            <div className="flex items-center justify-end space-x-2 pt-2">
              <button
                onClick={() => setConfirmSendEmailOpen(false)}
                className="px-4 py-2 border border-slate-200 text-xs font-bold text-slate-600 rounded-xl hover:bg-slate-50 transition-all cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setConfirmSendEmailOpen(false);
                  executeSendEmail();
                }}
                className="px-4 py-2 text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-xl transition-all cursor-pointer shadow-sm"
              >
                Send Now
              </button>
            </div>
          </div>
        </Modal>
      )}
    </Modal>
  );
}
